import React, { FC } from 'react';
import {
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    Paper,
    Button,
    Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DetailHourCell from './detail-hour-cell.component';
import { ScheduledStep } from 'models/production-planning';
import { Department } from 'models/organization';

type DetailGridTableProps = {
    department: Department;
    gridSteps: ScheduledStep[];
    hours: number[];
    onCellClick: (cellSteps: ScheduledStep[], stepName: string, hour: number) => void;
    onShowSchedule: (stepId: string) => void;
};

const DetailGridTable: FC<DetailGridTableProps> = ({ department, gridSteps, hours, onCellClick, onShowSchedule }) => {
    const { t: translate } = useTranslation(['production-dashboard']);

    const sortedWorkflowSteps = [...department.workflowSteps].sort((a, b) => a.stepOrder - b.stepOrder);

    // DD-3786: by step id, as an order's plan keeps the step name from when it was planned.
    const getCellSteps = (stepId: string, hour: number): ScheduledStep[] =>
        gridSteps.filter((s) => s.stepId === stepId && s.deadline.getHours() === hour);

    if (hours.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                {translate('production-dashboard:NO_SCHEDULED_STEPS')}
            </Typography>
        );
    }

    return (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ minWidth: 140 }}>{translate('production-dashboard:STEP')}</TableCell>
                        {hours.map((hour) => (
                            <TableCell key={hour} align="center" sx={{ minWidth: 60 }}>
                                {`${hour.toString().padStart(2, '0')}:00`}
                            </TableCell>
                        ))}
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedWorkflowSteps.map((ws) => (
                        <TableRow key={ws._id}>
                            <TableCell sx={{ fontWeight: 500, left: 0, backgroundColor: '#fff', zIndex: 1 }}>
                                {ws.name}
                            </TableCell>
                            {hours.map((hour) => {
                                const cellSteps = getCellSteps(ws._id, hour);
                                return (
                                    <DetailHourCell
                                        key={hour}
                                        cellSteps={cellSteps}
                                        onCellClick={() => onCellClick(cellSteps, ws.name, hour)}
                                    />
                                );
                            })}
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                <Button size="small" variant="text" onClick={() => onShowSchedule(ws._id)}>
                                    {translate('production-dashboard:SHOW_SCHEDULE')}
                                </Button>
                                <Button
                                    size="small"
                                    variant="text"
                                    component={RouterLink}
                                    to={`/workflow/orders?id=${ws._id}&name=${encodeURIComponent(ws.name)}`}
                                >
                                    {translate('production-dashboard:WORKFLOW_ORDER_LIST')}
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default DetailGridTable;
